import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './PaymentsManagement.css';

const PaymentsManagement = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'success', 'failed', 'refunded'
  const [refundingId, setRefundingId] = useState(null);
  const [refundModal, setRefundModal] = useState(null); // { paymentId, amount, orderNumber }
  const [refundAmount, setRefundAmount] = useState('');
  const [refundNotes, setRefundNotes] = useState('');

  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = filterStatus !== 'all' ? `?status=${filterStatus}` : '';
      const response = await api.get(`/admin/payments${params}`);
      setPayments(response.data.payments || []);
    } catch (err) {
      console.error('Error fetching payments:', err);
      setError(err.response?.data?.error || 'Failed to load payments. Please try again.');
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const handleRefundClick = (payment) => {
    if (payment.status !== 'success') {
      alert('Only successful payments can be refunded.');
      return;
    }
    setRefundModal({
      paymentId: payment.id,
      paymentAmount: parseFloat(payment.amount),
      orderNumber: payment.order_number,
      hdfcPaymentId: payment.hdfc_payment_id
    });
    setRefundAmount(payment.amount);
    setRefundNotes('');
  };

  const handleRefundSubmit = async () => {
    if (!refundModal) return;

    const amount = parseFloat(refundAmount);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid refund amount.');
      return;
    }

    if (amount > refundModal.paymentAmount) {
      alert(`Refund amount cannot exceed payment amount of ₹${refundModal.paymentAmount.toFixed(2)}`);
      return;
    }

    if (!window.confirm(`Are you sure you want to refund ₹${amount.toFixed(2)} for order ${refundModal.orderNumber}?`)) {
      return;
    }

    try {
      setRefundingId(refundModal.paymentId);
      const response = await api.post(`/payments/${refundModal.paymentId}/refund`, {
        amount: amount,
        notes: refundNotes || 'Admin refund'
      });

      alert(`Refund processed successfully! Refund ID: ${response.data.refund?.id || 'N/A'}`);
      setRefundModal(null);
      setRefundAmount('');
      setRefundNotes('');
      fetchPayments(); // Refresh list
    } catch (err) {
      console.error('Refund error:', err);
      alert(err.response?.data?.error || err.response?.data?.details || 'Failed to process refund. Please try again.');
    } finally {
      setRefundingId(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeClass = (status) => {
    const statusLower = status.toLowerCase();
    return `status-badge status-${statusLower}`;
  };

  if (loading) {
    return (
      <div className="App">
        <Header />
        <div className="payments-management-page">
          <div className="container">
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading payments...</p>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App">
      <Header />
      <div className="payments-management-page">
        <div className="container">
          <div className="page-header">
            <div className="header-content">
              <div className="header-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
              </div>
              <div>
                <h1 className="page-title">Payments Management</h1>
                <p className="page-subtitle">View and manage all payment transactions</p>
              </div>
            </div>
          </div>

          {error && (
            <div className="error-message">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <span>{error}</span>
            </div>
          )}

          {/* Filters */}
          <div className="filters-section">
            <div className="filter-group">
              <label>Filter by Status:</label>
              <select 
                value={filterStatus} 
                onChange={(e) => setFilterStatus(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Payments</option>
                <option value="success">Success</option>
                <option value="failed">Failed</option>
                <option value="refunded">Refunded</option>
                <option value="pending">Pending</option>
              </select>
            </div>
            <div className="stats-summary">
              <span className="stat-item">
                Total: <strong>{payments.length}</strong>
              </span>
              <span className="stat-item">
                Success: <strong>{payments.filter(p => p.status === 'success').length}</strong>
              </span>
              <span className="stat-item">
                Refunded: <strong>{payments.filter(p => p.status === 'refunded').length}</strong>
              </span>
            </div>
          </div>

          {/* Payments Table */}
          <div className="payments-table-container">
            {payments.length === 0 ? (
              <div className="empty-state">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
                <p>No payments found</p>
              </div>
            ) : (
              <table className="payments-table">
                <thead>
                  <tr>
                    <th>Payment ID</th>
                    <th>Order Number</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Payment Method</th>
                    <th>Transaction ID</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => (
                    <tr key={payment.id}>
                      <td className="payment-id">#{payment.id}</td>
                      <td>{payment.order_number || 'N/A'}</td>
                      <td className="amount-cell">₹{parseFloat(payment.amount).toFixed(2)}</td>
                      <td>
                        <span className={getStatusBadgeClass(payment.status)}>
                          {payment.status}
                        </span>
                      </td>
                      <td>
                        {payment.hdfc_payment_id ? (
                          <span className="payment-method-badge">HDFC Gateway</span>
                        ) : (
                          <span className="payment-method-badge">N/A</span>
                        )}
                      </td>
                      <td className="transaction-id">
                        {payment.hdfc_payment_id || payment.transaction_id || 'N/A'}
                      </td>
                      <td className="date-cell">{formatDate(payment.created_at)}</td>
                      <td className="actions-cell">
                        {payment.status === 'success' && payment.hdfc_payment_id && (
                          <button
                            className="btn-refund"
                            onClick={() => handleRefundClick(payment)}
                            disabled={refundingId === payment.id}
                          >
                            {refundingId === payment.id ? (
                              <>
                                <svg className="spinner" width="16" height="16" viewBox="0 0 24 24">
                                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" strokeDasharray="32" strokeDashoffset="32">
                                    <animate attributeName="stroke-dasharray" dur="2s" values="0 32;16 16;0 32;0 32" repeatCount="indefinite"/>
                                    <animate attributeName="stroke-dashoffset" dur="2s" values="0;-16;-32;-32" repeatCount="indefinite"/>
                                  </circle>
                                </svg>
                                Processing...
                              </>
                            ) : (
                              <>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                  <polyline points="17 8 12 3 7 8"></polyline>
                                  <line x1="12" y1="3" x2="12" y2="15"></line>
                                </svg>
                                Refund
                              </>
                            )}
                          </button>
                        )}
                        {payment.status === 'refunded' && payment.refund_id && (
                          <div className="refund-info">
                            <span className="refund-badge">Refunded</span>
                            {payment.refund_amount && (
                              <span className="refund-amount">₹{parseFloat(payment.refund_amount).toFixed(2)}</span>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Refund Modal */}
      {refundModal && (
        <div className="modal-overlay" onClick={() => setRefundModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Process Refund</h2>
              <button className="modal-close" onClick={() => setRefundModal(null)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="refund-details">
                <div className="detail-row">
                  <span className="detail-label">Order Number:</span>
                  <span className="detail-value">{refundModal.orderNumber}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Payment Amount:</span>
                  <span className="detail-value">₹{refundModal.paymentAmount.toFixed(2)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Transaction ID:</span>
                  <span className="detail-value">{refundModal.hdfcPaymentId}</span>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="refundAmount">Refund Amount (₹)</label>
                <input
                  type="number"
                  id="refundAmount"
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  min="0"
                  max={refundModal.paymentAmount}
                  step="0.01"
                  placeholder={`Max: ₹${refundModal.paymentAmount.toFixed(2)}`}
                />
                <small>Enter amount to refund (leave empty for full refund)</small>
              </div>

              <div className="form-group">
                <label htmlFor="refundNotes">Refund Reason/Notes</label>
                <textarea
                  id="refundNotes"
                  value={refundNotes}
                  onChange={(e) => setRefundNotes(e.target.value)}
                  rows="3"
                  placeholder="Enter reason for refund (optional)"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setRefundModal(null)}>
                Cancel
              </button>
              <button className="btn-confirm" onClick={handleRefundSubmit}>
                Process Refund
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default PaymentsManagement;

